/**
 * Route planning math: per-leg distance/time/fuel from a boat profile.
 * Rhumb-line legs (constant heading). Feeds the manual planner (spec 05).
 */

import { rhumbDistanceNm, rhumbBearingDeg, type LatLon } from './geo.js';

export interface BoatProfile {
  cruiseKn: number;
  fuelLPerNm: number;
}

export interface Leg {
  from: LatLon;
  to: LatLon;
  distanceNm: number;
  bearingDeg: number;
  durationMin: number;
  fuelL: number;
}

export interface RouteTotals {
  distanceNm: number;
  durationMin: number;
  fuelL: number;
}

export interface RoutePlan {
  legs: Leg[];
  totals: RouteTotals;
}

/** ETA epoch-ms = departure + total duration. Handles crossing midnight. */
export function routeEta(departureMs: number, totalDurationMin: number): number {
  return departureMs + totalDurationMin * 60_000;
}

export function computeRoute(waypoints: LatLon[], boat: BoatProfile): RoutePlan {
  const legs: Leg[] = [];
  const cruise = boat.cruiseKn > 0 ? boat.cruiseKn : NaN;

  for (let i = 1; i < waypoints.length; i++) {
    const from = waypoints[i - 1]!;
    const to = waypoints[i]!;
    const distanceNm = rhumbDistanceNm(from, to);
    const durationMin = (distanceNm / cruise) * 60;
    legs.push({
      from,
      to,
      distanceNm,
      bearingDeg: rhumbBearingDeg(from, to),
      durationMin,
      fuelL: distanceNm * boat.fuelLPerNm,
    });
  }

  const totals = legs.reduce<RouteTotals>(
    (acc, leg) => ({
      distanceNm: acc.distanceNm + leg.distanceNm,
      durationMin: acc.durationMin + leg.durationMin,
      fuelL: acc.fuelL + leg.fuelL,
    }),
    { distanceNm: 0, durationMin: 0, fuelL: 0 },
  );

  return { legs, totals };
}
